import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import allCourses from "../data/enrollmentCourses.json";
import { useOtpVerification } from "../hooks/useOtpVerification";
import OtpVerifyPanel from "./OtpVerifyPanel";

const API_URL = process.env.REACT_APP_API_URL || "";

// ─── Constants ───────────────────────────────────────────────────────────────

const ROLES = [
  { id: "SLP",       label: "Speech Language Pathologist", icon: "record_voice_over", sub: "Enhance therapy outcomes & certifications",    defaultLevel: "Intermediate" },
  { id: "Audiologist",label: "Audiologist",                icon: "hearing",           sub: "Advance diagnostics & treatment skills",        defaultLevel: "Intermediate" },
  { id: "Admin",     label: "Administration / Manager",    icon: "manage_accounts",   sub: "Grow and optimize your practice",               defaultLevel: "Intermediate" },
  { id: "Student",   label: "Student Intern",              icon: "school",            sub: "Build skills and gain real-world readiness",    defaultLevel: "Beginner" },
];

const GOALS = [
  { id: "Improve clinical skills",     icon: "clinical_notes",   label: "Improve clinical skills" },
  { id: "Earn certifications",         icon: "workspace_premium",label: "Earn certifications" },
  { id: "Manage or grow a practice",   icon: "trending_up",      label: "Manage or grow a practice" },
  { id: "Explore career pathways",     icon: "explore",          label: "Explore career pathways" },
  { id: "Internship / training support",icon: "co_present",      label: "Internship / training support" },
];

const LEVELS = [
  { id: "Beginner",     icon: "signal_cellular_1_bar", label: "Beginner",     sub: "Just getting started" },
  { id: "Intermediate", icon: "signal_cellular_2_bar", label: "Intermediate", sub: "Building confidence" },
  { id: "Advanced",     icon: "signal_cellular_alt",   label: "Advanced",     sub: "Looking to specialize" },
];

const TOTAL_STEPS = 4;

const ROLE_NUDGES = {
  SLP:        "Top clinicians typically focus on skill upgrades first",
  Audiologist:"Top clinicians typically focus on skill upgrades first",
  Admin:      "Most managers choose growth & operations training",
  Student:    "Most interns start with foundational certifications",
};

// ─── Slide Transition ─────────────────────────────────────────────────────────

function SlidePane({ children, stepKey }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), 20); return () => clearTimeout(t); }, [stepKey]);
  return (
    <div style={{
      opacity: visible ? 1 : 0,
      transform: visible ? "translateX(0)" : "translateX(24px)",
      transition: "opacity 0.28s ease, transform 0.28s ease",
    }}>
      {children}
    </div>
  );
}

// ─── Step 1: Role ─────────────────────────────────────────────────────────────

function StepRole({ onSelect }) {
  const [showNudge, setShowNudge] = useState(false);
  useEffect(() => { const t = setTimeout(() => setShowNudge(true), 5000); return () => clearTimeout(t); }, []);
  return (
    <SlidePane stepKey="role">
      <h3 style={s.stepHeading}>Let's start with you</h3>
      <p style={s.stepSub}>Choose the role that best matches your current work</p>
      {showNudge && (
        <div style={s.nudge}>
          <i className="material-symbols-outlined" style={{ fontSize: 16, color: "#0083D3" }}>tips_and_updates</i>
          Most clinicians start here to find the right learning path
        </div>
      )}
      <div style={s.cardGrid}>
        {ROLES.map(r => (
          <button key={r.id} type="button" onClick={() => onSelect(r)} style={s.roleCard} className="drawer-card">
            <div style={s.roleIcon}><i className="material-symbols-outlined" style={{ fontSize: 28, color: "#0083D3" }}>{r.icon}</i></div>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#00496D", marginBottom: 4 }}>{r.label}</div>
            <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.5 }}>{r.sub}</div>
          </button>
        ))}
      </div>
    </SlidePane>
  );
}

// ─── Step 2: Goal ─────────────────────────────────────────────────────────────

function StepGoal({ role, onSelect }) {
  return (
    <SlidePane stepKey="goal">
      <h3 style={s.stepHeading}>What do you want to achieve right now?</h3>
      <p style={s.stepSub}>We'll tailor recommendations instantly</p>
      {role && (
        <div style={s.nudge}>
          <i className="material-symbols-outlined" style={{ fontSize: 16, color: "#0083D3" }}>auto_awesome</i>
          {ROLE_NUDGES[role.id]}
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {GOALS.map(g => (
          <button key={g.id} type="button" onClick={() => onSelect(g)} style={s.listCard} className="drawer-card">
            <i className="material-symbols-outlined" style={{ fontSize: 20, color: "#0083D3", flexShrink: 0 }}>{g.icon}</i>
            <span style={{ fontWeight: 600, fontSize: 14, color: "#3A4252" }}>{g.label}</span>
            <i className="material-symbols-outlined ms-auto" style={{ fontSize: 18, color: "#d1d5db" }}>chevron_right</i>
          </button>
        ))}
      </div>
      <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 14, textAlign: "center" }}>You can change this anytime</p>
    </SlidePane>
  );
}

// ─── Step 3: Level ────────────────────────────────────────────────────────────

function StepLevel({ role, onSelect }) {
  const smartDefault = role?.defaultLevel;
  return (
    <SlidePane stepKey="level">
      <h3 style={s.stepHeading}>Where would you place yourself?</h3>
      <div style={{ ...s.nudge, marginBottom: 20 }}>
        <i className="material-symbols-outlined" style={{ fontSize: 16, color: "#0083D3" }}>psychology</i>
        We'll match the difficulty so you don't feel overwhelmed
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {LEVELS.map(l => (
          <button key={l.id} type="button" onClick={() => onSelect(l)} style={{
            ...s.listCard,
            borderColor: l.id === smartDefault ? "#0083D3" : "#eef2f7",
            background: l.id === smartDefault ? "rgba(0,131,211,0.04)" : "#fff",
          }} className="drawer-card">
            <i className="material-symbols-outlined" style={{ fontSize: 22, color: l.id === smartDefault ? "#0083D3" : "#9ca3af", flexShrink: 0 }}>{l.icon}</i>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#3A4252" }}>{l.label}</div>
              <div style={{ fontSize: 12, color: "#9ca3af" }}>{l.sub}</div>
            </div>
            {l.id === smartDefault && (
              <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 600, background: "rgba(0,131,211,0.1)", color: "#0083D3", padding: "2px 10px", borderRadius: 100 }}>Recommended</span>
            )}
          </button>
        ))}
      </div>
    </SlidePane>
  );
}

// ─── Step 4: Results ──────────────────────────────────────────────────────────

function StepResults({ role, goal, level, onEnroll, onViewCourse }) {
  const recs = allCourses.filter(c =>
    c.roles.includes(role?.id) &&
    c.goals.includes(goal?.id) &&
    c.levels.includes(level?.id)
  );
  const display = recs.length > 0 ? recs : allCourses.slice(0, 2);

  const badgeStyle = (badge) => ({
    display: "inline-flex", alignItems: "center", gap: 5,
    fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 100,
    marginBottom: 12,
    background: badge === "popular" ? "#FFF7ED" : badge === "rated" ? "#FFF1F2" : "#F0FDF4",
    color: badge === "popular" ? "#ea580c" : badge === "rated" ? "#e11d48" : "#16a34a",
  });

  return (
    <SlidePane stepKey="results">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
        <h3 style={{ ...s.stepHeading, marginBottom: 0 }}>Here's what fits you best</h3>
      </div>
      <p style={s.stepSub}>Based on your role and goals — <strong style={{ color: "#0083D3" }}>personalized for you</strong></p>

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {display.map(c => (
          <div key={c.id} style={s.courseCard}>
            <span style={badgeStyle(c.badge)}>{c.badgeText}</span>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 8 }}>
              <h4 style={{ fontSize: 15, fontWeight: 700, color: "#00496D", lineHeight: 1.4, margin: 0 }}>{c.title}</h4>
              <span style={{ fontSize: 11, fontWeight: 600, background: "rgba(0,131,211,0.08)", color: "#0083D3", padding: "3px 10px", borderRadius: 100, whiteSpace: "nowrap", flexShrink: 0 }}>
                Recommended for you
              </span>
            </div>
            <p style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.65, marginBottom: 12 }}>{c.description}</p>
            <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
              {[
                { icon: "schedule", val: c.duration },
                { icon: "view_module", val: c.modules },
                { icon: "signal_cellular_alt", val: c.level },
              ].map(m => (
                <div key={m.val} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <i className="material-symbols-outlined" style={{ fontSize: 15, color: "#9ca3af" }}>{m.icon}</i>
                  <span style={{ fontSize: 12, color: "#6b7280" }}>{m.val}</span>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button type="button" onClick={() => onViewCourse(c.path)} style={s.btnOutline}>View Course</button>
              <button type="button" onClick={() => onEnroll(c)} style={s.btnPrimary}>Enrol Now →</button>
            </div>
          </div>
        ))}
      </div>

      <p style={{ fontSize: 12, color: "#0083D3", textAlign: "center", marginTop: 16, fontWeight: 600 }}>
        Start learning today — no wait
      </p>
    </SlidePane>
  );
}

// ─── Step 5: Lead Capture ─────────────────────────────────────────────────────

function StepLead({ onSubmit, onSkip, isSending }) {
  const [leadStep, setLeadStep] = useState("form"); // "form" | "otp"
  const [name, setName]   = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [optin, setOptin] = useState(true);
  const [error, setError] = useState("");
  const otp = useOtpVerification();
  const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

  useEffect(() => {
    if (otp.step === "verified") {
      onSubmit({ name, email, phone, optin }, otp.verifiedToken);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp.step]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim())    { setError("Please enter your name."); return; }
    if (!isEmail(email)) { setError("Please enter a valid email address."); return; }
    setError("");
    await otp.send(email, name);
    setLeadStep("otp");
  }

  return (
    <SlidePane stepKey="lead">
      <h3 style={s.stepHeading}>Want us to save your recommendations?</h3>
      <p style={s.stepSub}>We'll send your personalized course list straight to your inbox.</p>

      {/* OTP verification panel */}
      {leadStep === "otp" && (
        <div style={{ marginBottom: 16 }}>
          <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#0083D3", margin: "0 0 8px" }}>Verify your email</p>
          <OtpVerifyPanel otp={otp} email={email} name={name} onBack={() => { setLeadStep("form"); otp.reset(); }} />
        </div>
      )}

      {/* Form */}
      {leadStep === "form" && (
        <form onSubmit={handleSubmit} noValidate>
          <div style={s.field}>
            <label style={s.label}>Full Name</label>
            <input style={s.input} type="text" placeholder="e.g. Sarah Johnson" value={name}
              onChange={e => { setName(e.target.value); setError(""); }} autoComplete="name" />
          </div>
          <div style={s.field}>
            <label style={s.label}>Email</label>
            <div style={{ position: "relative" }}>
              <input style={s.input} type="email" placeholder="you@clinic.ca" value={email}
                onChange={e => { setEmail(e.target.value); setError(""); }} autoComplete="email" />
              {isEmail(email) && (
                <i className="material-symbols-outlined" style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: 18, color: "#22c55e" }}>check_circle</i>
              )}
            </div>
          </div>
          <div style={s.field}>
            <label style={s.label}>Phone <span style={{ color: "#9ca3af", fontWeight: 400 }}>(optional — for reminders)</span></label>
            <input style={s.input} type="tel" placeholder="+1 (416) 000-0000" value={phone}
              onChange={e => setPhone(e.target.value)} autoComplete="tel" />
          </div>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 20, cursor: "pointer" }}>
            <input type="checkbox" checked={optin} onChange={e => setOptin(e.target.checked)}
              style={{ marginTop: 3, accentColor: "#0083D3", width: 16, height: 16, flexShrink: 0 }} />
            <span style={{ fontSize: 13, color: "#4b5563", lineHeight: 1.5 }}>
              Send me personalized course suggestions <span style={{ color: "#9ca3af" }}>(no spam)</span>
            </span>
          </label>
          {error && <p style={{ fontSize: 13, color: "#ef4444", marginBottom: 12 }}>{error}</p>}
          {otp.error && <p style={{ fontSize: 13, color: "#ef4444", marginBottom: 12 }}>{otp.error}</p>}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <i className="material-symbols-outlined" style={{ fontSize: 16, color: "#9ca3af" }}>shield</i>
            <span style={{ fontSize: 12, color: "#9ca3af" }}>We'll verify your email before sending.</span>
          </div>
          <button type="submit" disabled={otp.step === "sending" || isSending}
            style={{ ...s.btnPrimary, width: "100%", justifyContent: "center", height: 48, opacity: (otp.step === "sending" || isSending) ? 0.75 : 1 }}>
            {otp.step === "sending" || isSending ? (
              <><i className="material-symbols-outlined" style={{ fontSize: 18, animation: "spin 1s linear infinite" }}>progress_activity</i>Sending code…</>
            ) : "Continue →"}
          </button>
        </form>
      )}

      {leadStep === "form" && (
        <button type="button" onClick={onSkip} style={{ ...s.btnGhost, width: "100%", marginTop: 10 }}>
          Not ready yet? Save for later
        </button>
      )}
    </SlidePane>
  );
}

// ─── Step 6: Final CTA ────────────────────────────────────────────────────────

function StepFinal({ lead, selectedCourse, onClose }) {
  const navigate = useNavigate();
  const emailSent = !!lead?.email;
  return (
    <SlidePane stepKey="final">
      <div style={{ textAlign: "center", padding: "8px 0 28px" }}>
        {/* Success icon */}
        <div style={{ width: 72, height: 72, borderRadius: "50%", background: "rgba(34,197,94,0.1)", border: "2px solid rgba(34,197,94,0.25)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
          <i className="material-symbols-outlined" style={{ fontSize: 36, color: "#16a34a" }}>mark_email_read</i>
        </div>
        <h3 style={{ ...s.stepHeading, textAlign: "center", marginBottom: 8 }}>
          {emailSent ? "Recommendations sent!" : "You're all set!"}
        </h3>
        {emailSent ? (
          <>
            <p style={{ ...s.stepSub, textAlign: "center", marginBottom: 6 }}>
              A copy has been sent to <strong style={{ color: "#00496D" }}>{lead.email}</strong>
            </p>
            <p style={{ fontSize: 12, color: "#9ca3af", textAlign: "center", marginBottom: 0 }}>
              Our team at <strong>compliances@neuraspeech.com</strong> will follow up with you shortly.
            </p>
          </>
        ) : (
          <p style={{ ...s.stepSub, textAlign: "center" }}>
            Your personalized recommendations are ready.
          </p>
        )}
      </div>

      {/* Confirmation card */}
      {emailSent && (
        <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 12, padding: "14px 18px", marginBottom: 20, display: "flex", alignItems: "flex-start", gap: 12 }}>
          <i className="material-symbols-outlined" style={{ fontSize: 20, color: "#16a34a", flexShrink: 0, marginTop: 1 }}>check_circle</i>
          <div>
            <p style={{ fontSize: 13, fontWeight: 600, color: "#166534", margin: "0 0 2px" }}>Email delivered</p>
            <p style={{ fontSize: 12, color: "#4b5563", margin: 0 }}>Check your inbox — we've CC'd you on your course recommendations.</p>
          </div>
        </div>
      )}

      <button type="button"
        onClick={() => { onClose(); if (selectedCourse) navigate(selectedCourse.path); else navigate("/courses"); }}
        style={{ ...s.btnPrimary, width: "100%", justifyContent: "center", height: 50, fontSize: 15, marginBottom: 12 }}>
        <i className="material-symbols-outlined" style={{ fontSize: 20 }}>arrow_forward</i>
        Continue to Enrolment
      </button>
      <button type="button" onClick={onClose} style={{ ...s.btnGhost, width: "100%", justifyContent: "center" }}>
        Close
      </button>
    </SlidePane>
  );
}

// ─── Exit Intent Dialog ───────────────────────────────────────────────────────

function ExitDialog({ onStay, onLeave }) {
  return (
    <div style={{ position: "absolute", inset: 0, background: "rgba(255,255,255,0.96)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", zIndex: 10, padding: 32, textAlign: "center", borderRadius: 20 }}>
      <i className="material-symbols-outlined" style={{ fontSize: 40, color: "#0083D3", marginBottom: 16 }}>mark_email_unread</i>
      <h4 style={{ fontSize: 18, fontWeight: 700, color: "#00496D", marginBottom: 10 }}>Want us to email your results before you go?</h4>
      <p style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.6, marginBottom: 24 }}>
        We'll save your personalized recommendations so you can come back anytime.
      </p>
      <button type="button" onClick={onStay} style={{ ...s.btnPrimary, marginBottom: 10, justifyContent: "center", width: "100%", height: 46 }}>
        Keep going — I'm almost done
      </button>
      <button type="button" onClick={onLeave} style={{ ...s.btnGhost, width: "100%", justifyContent: "center" }}>
        No thanks, I'll leave
      </button>
    </div>
  );
}

// ─── Main Drawer ──────────────────────────────────────────────────────────────

function GuidedEnrollmentDrawer({ onClose }) {
  const [step, setStep]               = useState(1);
  const [role, setRole]               = useState(null);
  const [goal, setGoal]               = useState(null);
  const [level, setLevel]             = useState(null);
  const [lead, setLead]               = useState(null);
  const [selectedCourse, setSelected] = useState(null);
  const [showExit, setShowExit]       = useState(false);
  const [isSending, setIsSending]     = useState(false);
  const drawerRef = useRef(null);

  async function handleLeadSubmit(data, verifiedToken) {
    setIsSending(true);
    try {
      await fetch(`${API_URL}/lead-capture`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name:         data.name,
          email:        data.email,
          phone:        data.phone || "",
          role:         role?.label  || "Not selected",
          goal:         goal?.label  || "Not selected",
          level:        level?.id    || "Not selected",
          optin:        data.optin,
          verifiedToken,
        }),
      });
    } catch (err) {
      console.error("Lead capture error:", err);
    } finally {
      setIsSending(false);
      setLead(data);
      setStep(6);
    }
  }

  function attemptClose() {
    if (step < 5) { setShowExit(true); } else { onClose(); }
  }

  // Close on backdrop
  const handleBackdrop = useCallback((e) => {
    if (e.target === e.currentTarget) attemptClose();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Escape key
  useEffect(() => {
    const fn = (e) => { if (e.key === "Escape") attemptClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Body scroll lock
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  function selectRole(r) {
    setRole(r);
    setLevel({ id: r.defaultLevel });
    setTimeout(() => setStep(2), 120);
  }

  function selectGoal(g) {
    setGoal(g);
    setTimeout(() => setStep(3), 120);
  }

  function selectLevel(l) {
    setLevel(l);
    setTimeout(() => setStep(4), 120);
  }

  const progressPct = Math.min(((step - 1) / TOTAL_STEPS) * 100, 100);
  const stepLabels  = ["", "Your Role", "Your Goal", "Experience", "Courses", "Details", "Done"];

  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 99999, display: "flex", justifyContent: "flex-end", background: "rgba(0,0,0,0.35)", backdropFilter: "blur(2px)" }}
      onClick={handleBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label="Guided Enrollment"
    >
      <div
        ref={drawerRef}
        style={{
          width: "100%",
          maxWidth: 480,
          height: "100%",
          background: "#FAFBFC",
          display: "flex",
          flexDirection: "column",
          boxShadow: "-8px 0 48px rgba(0,0,0,0.14)",
          position: "relative",
          overflowY: "auto",
          animation: "slideInDrawer 0.3s ease",
        }}
        className="guided-drawer-panel"
      >
        {showExit && <ExitDialog onStay={() => setShowExit(false)} onLeave={onClose} />}

        {/* Header */}
        <div style={{ padding: "24px 28px 0", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(0,131,211,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="material-symbols-outlined" style={{ fontSize: 18, color: "#0083D3" }}>auto_awesome</i>
              </div>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#00496D", letterSpacing: "0.02em" }}>GUIDED ENROLMENT</span>
            </div>
            <button type="button" onClick={attemptClose} style={{ background: "none", border: "none", cursor: "pointer", padding: 4, borderRadius: 6, color: "#9ca3af" }} aria-label="Close">
              <i className="material-symbols-outlined" style={{ fontSize: 22 }}>close</i>
            </button>
          </div>

          {/* Progress */}
          {step <= TOTAL_STEPS && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "#0083D3" }}>
                  Step {step} of {TOTAL_STEPS} — {stepLabels[step]}
                </span>
                <span style={{ fontSize: 12, color: "#9ca3af" }}>
                  {step < TOTAL_STEPS ? "Almost there" : "Last step"}
                </span>
              </div>
              <div style={{ height: 4, background: "#eef2f7", borderRadius: 100, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${progressPct}%`, background: "linear-gradient(90deg,#00496D,#0083D3)", borderRadius: 100, transition: "width 0.4s ease" }} />
              </div>
              {step === 3 && (
                <p style={{ fontSize: 12, color: "#0083D3", marginTop: 8, fontWeight: 500 }}>
                  ✓ Great — we're narrowing this down for you
                </p>
              )}
            </div>
          )}
        </div>

        {/* Step content */}
        <div style={{ flex: 1, padding: "0 28px 32px", overflowY: "auto" }}>
          {step === 1 && <StepRole onSelect={selectRole} />}
          {step === 2 && <StepGoal role={role} onSelect={selectGoal} />}
          {step === 3 && <StepLevel role={role} onSelect={selectLevel} />}
          {step === 4 && (
            <StepResults
              role={role} goal={goal} level={level}
              onEnroll={(c) => { setSelected(c); setStep(5); }}
              onViewCourse={(path) => { onClose(); window.location.href = path; }}
            />
          )}
          {step === 5 && (
            <StepLead
              onSubmit={handleLeadSubmit}
              onSkip={onClose}
              isSending={isSending}
            />
          )}
          {step === 6 && <StepFinal lead={lead} selectedCourse={selectedCourse} onClose={onClose} />}
        </div>

        {/* Back nav */}
        {step > 1 && step <= TOTAL_STEPS && (
          <div style={{ padding: "12px 28px 20px", borderTop: "1px solid #eef2f7", flexShrink: 0 }}>
            <button type="button" onClick={() => setStep(s => s - 1)} style={s.btnGhost}>
              <i className="material-symbols-outlined" style={{ fontSize: 16 }}>arrow_back</i>
              Back
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideInDrawer {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        .drawer-card:hover {
          border-color: #0083D3 !important;
          box-shadow: 0 4px 16px rgba(0,131,211,0.12) !important;
          transform: translateY(-1px);
        }
        .drawer-card { transition: all 0.15s ease !important; }
        @media (max-width: 767px) {
          .guided-drawer-panel {
            max-width: 100% !important;
            border-radius: 0 !important;
          }
          .drawer-card { padding: 12px !important; }
        }
      `}</style>
    </div>
  );
}

// ─── Inline text link trigger (under Explore Courses) ────────────────────────

export function GuidedEnrollmentInlineTrigger() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 7 }}>
        <i className="material-symbols-outlined" style={{ fontSize: 16, color: "#0083D3" }}>auto_awesome</i>
        <span style={{ fontSize: 14, color: "#6b7280" }}>Still need help?</span>
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 14, fontWeight: 600, color: "#0083D3", textDecoration: "underline", textUnderlineOffset: 3 }}
        >
          We can help you choose →
        </button>
      </div>
      {open && <GuidedEnrollmentDrawer onClose={() => setOpen(false)} />}
    </>
  );
}

// ─── Fixed floating trigger (right side, stacked above Light/Dark) ────────────

export function GuidedEnrollmentTrigger() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <style>{`
        .guided-float-btn {
          background: linear-gradient(135deg,#00496D,#0083D3);
          color: #fff;
          border: none;
          border-radius: 10px 0 0 10px;
          padding: 15px;
          cursor: pointer;
          position: fixed;
          right: 0;
          width: 137px;
          top: calc(50% - 62px);
          box-shadow: rgba(100,100,111,0.2) 0px 7px 29px 0px;
          z-index: 8000;
          display: flex;
          align-items: center;
          gap: 8px;
          text-align: left;
          transition: background 0.2s;
        }
        .guided-float-btn:hover {
          background: linear-gradient(135deg,#003a58,#006fb5);
        }
        /* Mobile: hide both side tabs below 768px, show bottom bar instead */
        @media (max-width: 767px) {
          .guided-float-btn {
            display: none;
          }
          .guided-mobile-bar {
            display: flex !important;
          }
        }
        .guided-mobile-bar {
          display: none;
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 8000;
          background: linear-gradient(135deg,#00496D,#0083D3);
          color: #fff;
          padding: 14px 20px;
          align-items: center;
          justify-content: space-between;
          box-shadow: 0 -4px 20px rgba(0,73,109,0.2);
          border-top: 1px solid rgba(255,255,255,0.1);
        }
      `}</style>

      {/* Desktop — fixed tab above Light/Dark switcher */}
      <button
        type="button"
        className="guided-float-btn"
        onClick={() => setOpen(true)}
        aria-label="Open guided enrollment"
      >
        <i className="material-symbols-outlined" style={{ fontSize: 18, flexShrink: 0 }}>auto_awesome</i>
        <div>
          <div style={{ fontWeight: 700, fontSize: 11, lineHeight: 1.3 }}>Not sure where to start?</div>
          <div style={{ fontSize: 10, opacity: 0.85, marginTop: 2 }}>We'll guide you · 60s</div>
        </div>
      </button>

      {/* Mobile — sticky bottom bar */}
      <div className="guided-mobile-bar" role="complementary">
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Not sure where to start?</div>
          <div style={{ fontSize: 12, opacity: 0.85 }}>We'll help you choose the right course</div>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{
            background: "#fff",
            color: "#00496D",
            border: "none",
            borderRadius: 8,
            padding: "10px 18px",
            fontWeight: 700,
            fontSize: 13,
            cursor: "pointer",
            flexShrink: 0,
            whiteSpace: "nowrap",
          }}
        >
          Guide Me →
        </button>
      </div>

      {open && <GuidedEnrollmentDrawer onClose={() => setOpen(false)} />}
    </>
  );
}

// ─── Shared styles ────────────────────────────────────────────────────────────

const s = {
  stepHeading: { fontSize: 22, fontWeight: 800, color: "#00496D", marginBottom: 6, lineHeight: 1.3 },
  stepSub:     { fontSize: 14, color: "#6b7280", marginBottom: 20, lineHeight: 1.6 },
  nudge: {
    display: "flex", alignItems: "center", gap: 8,
    background: "rgba(0,131,211,0.07)", border: "1px solid rgba(0,131,211,0.15)",
    borderRadius: 8, padding: "9px 14px", fontSize: 13, color: "#0083D3",
    fontWeight: 500, marginBottom: 18, lineHeight: 1.4
  },
  cardGrid:  { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  roleCard:  {
    background: "#fff", border: "1.5px solid #eef2f7", borderRadius: 14,
    padding: "18px 14px", cursor: "pointer", textAlign: "left",
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
  },
  roleIcon:  {
    width: 44, height: 44, borderRadius: 10,
    background: "rgba(0,131,211,0.08)",
    display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 10
  },
  listCard: {
    display: "flex", alignItems: "center", gap: 12,
    background: "#fff", border: "1.5px solid #eef2f7",
    borderRadius: 12, padding: "14px 16px", cursor: "pointer",
    textAlign: "left", boxShadow: "0 1px 4px rgba(0,0,0,0.04)"
  },
  courseCard: {
    background: "#fff", border: "1.5px solid #eef2f7",
    borderRadius: 16, padding: "20px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.05)"
  },
  btnPrimary: {
    display: "inline-flex", alignItems: "center", gap: 8,
    background: "linear-gradient(135deg,#00496D,#0083D3)",
    color: "#fff", border: "none", borderRadius: 10,
    padding: "10px 20px", fontWeight: 700, fontSize: 14,
    cursor: "pointer"
  },
  btnOutline: {
    display: "inline-flex", alignItems: "center", gap: 6,
    background: "transparent", border: "1.5px solid #0083D3",
    color: "#0083D3", borderRadius: 10,
    padding: "10px 18px", fontWeight: 600, fontSize: 14,
    cursor: "pointer"
  },
  btnGhost: {
    display: "inline-flex", alignItems: "center", gap: 6,
    background: "none", border: "none", color: "#9ca3af",
    fontSize: 13, cursor: "pointer", fontWeight: 500, padding: "6px 0"
  },
  field: { marginBottom: 16 },
  label: { display: "block", fontSize: 13, fontWeight: 600, color: "#3A4252", marginBottom: 6 },
  input: {
    width: "100%", height: 48, border: "1.5px solid #e5e7eb",
    borderRadius: 10, padding: "0 14px", fontSize: 14, color: "#1f2937",
    outline: "none", background: "#fff", boxSizing: "border-box"
  },
};

export default GuidedEnrollmentDrawer;
